/**
 * SynAssistant (AgencyAnalytics' AgencyAI): a drawer on the right. It greets
 * the person by name with suggestions for where they are (a client, or the
 * whole agency), keeps conversations, and has History, Tools and Skills
 * views. Questions can take a little while: Claude may read several sources.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { assistantApi, type ConversationView, type Skill } from './assistant-api.ts';
import { AssistantComposer } from './AssistantComposer.tsx';
import { HistoryPanel, SkillsPanel, ToolsPanel } from './AssistantPanels.tsx';
import { AssistantThread } from './AssistantThread.tsx';
import { SchedulesPanel } from './SchedulesPanel.tsx';

type View = 'chat' | 'history' | 'schedules' | 'tools' | 'skills';

const SUGGESTIONS = {
  client: [
    ['meeting-notes', 'Prepare notes for my client meeting'],
    ['health-check', 'Run a client health check'],
    ['metric-change', 'Investigate a metric change'],
    ['update-email', 'Draft a client update email'],
    ['analyze-data', 'Summarise last month’s results'],
  ],
  agency: [
    ['at-risk', 'Find at-risk clients'],
    ['client-wins', 'Celebrate client wins'],
    ['prospect', 'Research a prospect'],
  ],
} as const;

export function AssistantDrawer(props: {
  organisationId: string;
  firstName: string;
  client: { id: string; name: string } | null;
  canManage: boolean;
  /** Open this conversation first (a scheduled answer from the inbox). */
  conversationId?: string | null | undefined;
  onClose: () => void;
}): ReactNode {
  const [view, setView] = useState<View>('chat');
  const [current, setCurrent] = useState<ConversationView | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const { organisationId } = props;
  useEffect(() => {
    assistantApi.skills(organisationId).then(setSkills, () => setSkills([]));
  }, [organisationId]);
  // A new place means a new conversation, as AgencyAnalytics scopes chats to a client.
  useEffect(() => setCurrent(null), [props.client?.id]);
  useEffect(() => {
    if (!props.conversationId) return;
    setView('chat');
    assistantApi
      .conversation(organisationId, props.conversationId)
      .then(setCurrent, () => setProblem('That conversation could not be opened.'));
  }, [organisationId, props.conversationId]);

  const run = (input: Parameters<typeof assistantApi.turn>[1], optimistic?: string) => {
    setBusy(true);
    setProblem(null);
    if (optimistic)
      setCurrent((before) => ({
        conversation: before?.conversation ?? {
          id: '',
          title: optimistic,
          client_id: props.client?.id ?? null,
          source: 'CHAT',
          updated_at: '',
        },
        messages: [
          ...(before?.messages ?? []),
          { role: 'user', text: optimistic, tools: [], sources: [] },
        ],
        pending: null,
      }));
    assistantApi
      .turn(organisationId, {
        conversation_id: current?.conversation.id || null,
        client_id: props.client?.id ?? null,
        ...input,
      })
      .then(setCurrent)
      .catch((error: unknown) =>
        setProblem(
          error instanceof Error ? error.message : 'SynAssistant could not answer. Try again.',
        ),
      )
      .finally(() => setBusy(false));
  };
  const ask = (text: string, skill: Skill | null) =>
    run({ text, skill_id: skill?.id ?? null }, text);
  const suggestion = (skillId: string, label: string) =>
    ask(label, skills.find((skill) => skill.id === skillId) ?? null);
  const open = (id: string) => {
    setView('chat');
    assistantApi
      .conversation(organisationId, id)
      .then(setCurrent, () => setProblem('That conversation could not be opened.'));
  };
  const tabs: [View, string][] = [
    ['history', 'History'],
    ['schedules', 'Schedules'],
    ['skills', 'Skills'],
    ['tools', 'Tools'],
  ];

  return (
    <aside className="as" aria-labelledby="as-title">
      <header className="as-head">
        <div className="as-head-row">
          <span className="as-mark" aria-hidden="true">
            ✦
          </span>
          <h2 id="as-title">SynAssistant</h2>
          <button
            type="button"
            className="as-new"
            onClick={() => {
              setView('chat');
              setCurrent(null);
            }}
          >
            + New
          </button>
          <button
            type="button"
            className="as-close"
            aria-label="Close SynAssistant"
            onClick={props.onClose}
          >
            ×
          </button>
        </div>
        <nav className="as-tabs" aria-label="SynAssistant views">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`as-tab${view === key ? ' on' : ''}`}
              aria-pressed={view === key}
              onClick={() => setView(view === key ? 'chat' : key)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>
      {view === 'history' && <HistoryPanel organisationId={organisationId} onOpen={open} />}
      {view === 'schedules' && (
        <SchedulesPanel organisationId={organisationId} client={props.client} />
      )}
      {view === 'tools' && (
        <ToolsPanel organisationId={organisationId} canManage={props.canManage} />
      )}
      {view === 'skills' && (
        <SkillsPanel organisationId={organisationId} skills={skills} onChanged={setSkills} />
      )}
      {view === 'chat' && (
        <>
          {!current ? (
            <div className="as-hello">
              <p className="as-greeting">
                Hi {props.firstName || 'there'}, how can I help
                {props.client ? ` with ${props.client.name}` : ''}?
              </p>
              <ul className="as-suggestions">
                {SUGGESTIONS[props.client ? 'client' : 'agency'].map(([skillId, label]) => (
                  <li key={skillId}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => suggestion(skillId, label)}
                    >
                      <span>{label}</span>
                      <span aria-hidden="true">→</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <AssistantThread
              messages={current.messages}
              pending={current.pending}
              busy={busy}
              canDecide={props.canManage}
              onDecide={(decision) => run({ decision })}
            />
          )}
          {problem && (
            <p className="as-problem" role="alert">
              {problem}
            </p>
          )}
          <AssistantComposer
            skills={skills}
            inClient={Boolean(props.client)}
            busy={busy || Boolean(current?.pending?.length)}
            onSend={ask}
          />
        </>
      )}
    </aside>
  );
}
